// MentionInput — May 7, 2026
//
// Single-line input with @-mention autocomplete. Type `@`, get a dropdown of
// active team_roster members; arrow keys + Enter (or click) to select.
// Selecting inserts a `@[Full Name](uuid)` token at the cursor.
//
// Behavior notes:
// * Backspace at the end of a complete mention token deletes the WHOLE token
//   atomically (no piece-by-piece corruption of the @[Name](uuid) form).
// * Enter selects an active suggestion when the dropdown is open; otherwise
//   it fires `onSubmit` (matching the existing CoachingTab "Enter to send"
//   pattern).
// * Escape closes the dropdown without submitting.
// * The trigger window for the dropdown is the substring from the most recent
//   `@` to the cursor, *if* that substring contains no whitespace and isn't
//   already a complete token. This lets the user write "email greg@x.com"
//   naturally — a `@` preceded by a non-space character won't open the picker.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ChangeEvent,
} from "react";
import { supabase } from "../../lib/supabase";
import { formatMentionToken } from "../../lib/coachingMentions";

// Token regex matched ENDING AT a position — used by atomic-backspace handling.
const TOKEN_END_RE =
  /@\[([^\]\n]+)\]\(([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)$/;

export interface MentionMember {
  id: string;
  full_name: string;
  role: string;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Tailwind classes appended to the input. Caller controls width/padding. */
  inputClassName?: string;
  /** Don't include this user_id in the suggestion list (e.g. yourself). */
  excludeUserId?: string | null;
  autoFocus?: boolean;
}

/**
 * Module-scoped cache: team_roster doesn't change often during a session, so
 * we fetch once and reuse. Many CoachingTab instances open and close as the
 * user navigates between deals — caching avoids a duplicate fetch per mount.
 */
let rosterCache: MentionMember[] | null = null;
let rosterPromise: Promise<MentionMember[]> | null = null;

async function loadActiveRoster(): Promise<MentionMember[]> {
  if (rosterCache) return rosterCache;
  if (rosterPromise) return rosterPromise;
  rosterPromise = (async () => {
    const { data, error } = await supabase
      .from("team_roster")
      .select("id, full_name, role")
      .eq("is_active", true)
      .order("full_name");
    if (error || !data) {
      rosterPromise = null;
      return [];
    }
    rosterCache = data as MentionMember[];
    return rosterCache;
  })();
  return rosterPromise;
}

interface TriggerState {
  /** Char index of the `@` that opened the trigger. */
  start: number;
  /** Substring from `@`+1 up to the caret (the "what they've typed so far"). */
  query: string;
}

/**
 * Inspect (value, caret) and decide whether the user is currently composing a
 * mention. Returns null when no trigger is active. The trigger is the most
 * recent `@` preceding the caret IF (a) the char before the `@` is start-of-
 * string or whitespace, and (b) the substring between `@` and caret has no
 * whitespace, and (c) we're not inside a completed token.
 */
function detectTrigger(value: string, caret: number): TriggerState | null {
  // Walk back from the caret. Bail on whitespace or start-of-string.
  let i = caret - 1;
  while (i >= 0) {
    const c = value[i];
    if (c === "@") {
      const prev = i === 0 ? " " : value[i - 1];
      if (/\s/.test(prev)) {
        const query = value.slice(i + 1, caret);
        // If the query contains a `]` we're inside / past a completed token's
        // display name — bail.
        if (query.includes("]") || query.includes("(")) return null;
        return { start: i, query };
      }
      return null;
    }
    if (/\s/.test(c)) return null;
    i -= 1;
  }
  return null;
}

/**
 * Score a candidate name against the typed query. Higher = better match.
 * Prioritizes (1) prefix match on full name, (2) prefix match on any word,
 * (3) substring match. Case-insensitive throughout.
 */
function scoreMatch(name: string, query: string): number {
  if (!query) return 1; // anything ranks above 0 when no query is typed
  const n = name.toLowerCase();
  const q = query.toLowerCase();
  if (n.startsWith(q)) return 100;
  const words = n.split(/\s+/);
  if (words.some((w) => w.startsWith(q))) return 50;
  if (n.includes(q)) return 10;
  return 0;
}

export default function MentionInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  inputClassName,
  excludeUserId,
  autoFocus,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [members, setMembers] = useState<MentionMember[]>([]);
  const [trigger, setTrigger] = useState<TriggerState | null>(null);
  const [highlightIdx, setHighlightIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadActiveRoster().then((rows) => {
      if (!cancelled) setMembers(rows);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const suggestions = useMemo<MentionMember[]>(() => {
    if (!trigger) return [];
    const q = trigger.query;
    const scored = members
      .filter((m) => m.id !== excludeUserId)
      .map((m) => ({ m, s: scoreMatch(m.full_name, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.m.full_name.localeCompare(b.m.full_name))
      .slice(0, 6)
      .map((x) => x.m);
    return scored;
  }, [trigger, members, excludeUserId]);

  // When the suggestion set changes, reset the highlight to the top.
  useEffect(() => {
    setHighlightIdx(0);
  }, [trigger?.query, suggestions.length]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    onChange(next);
    const el = e.target;
    const caret = el.selectionStart ?? next.length;
    setTrigger(detectTrigger(next, caret));
  }

  function selectSuggestion(member: MentionMember) {
    if (!trigger) return;
    const before = value.slice(0, trigger.start);
    const afterCaret = value.slice((inputRef.current?.selectionStart ?? value.length));
    const token = formatMentionToken(member.full_name, member.id);
    // Always put a single space after the token so the next character the user
    // types is not interpreted as part of a new trigger.
    const next = before + token + " " + afterCaret;
    onChange(next);
    setTrigger(null);
    // Restore focus and put caret right after the inserted token+space.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const pos = before.length + token.length + 1;
      el.focus();
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        /* iOS Safari occasionally rejects setSelectionRange on a re-rendered input */
      }
    });
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (trigger && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const choice = suggestions[highlightIdx] ?? suggestions[0];
        if (choice) selectSuggestion(choice);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setTrigger(null);
        return;
      }
    }
    // Atomic Backspace: if the cursor sits at the end of a complete mention
    // token and there's no selection, delete the whole token in one go.
    if (e.key === "Backspace" && inputRef.current) {
      const el = inputRef.current;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      if (start === end && start > 0) {
        const left = value.slice(0, start);
        const m = TOKEN_END_RE.exec(left);
        if (m) {
          e.preventDefault();
          const newLeft = left.slice(0, left.length - m[0].length);
          const next = newLeft + value.slice(end);
          onChange(next);
          requestAnimationFrame(() => {
            const pos = newLeft.length;
            try {
              el.setSelectionRange(pos, pos);
            } catch { /* noop */ }
          });
          return;
        }
      }
    }
    // Enter (no active dropdown) → submit. Matches the existing CoachingTab
    // single-line "Enter sends" UX.
    if (e.key === "Enter" && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Delay so a click on the dropdown registers before we close it.
          setTimeout(() => setTrigger(null), 120);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        className={
          "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 " +
          (inputClassName ?? "")
        }
      />
      {trigger && suggestions.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
          // Capture mousedown so input's blur fires AFTER we handle the click.
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="px-3 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide bg-slate-50 border-b border-slate-100">
            Tag a teammate
          </div>
          {suggestions.map((m, i) => {
            const active = i === highlightIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => selectSuggestion(m)}
                onMouseEnter={() => setHighlightIdx(i)}
                className={
                  "w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition " +
                  (active ? "bg-teal-50 text-bardin-teal" : "text-slate-700 hover:bg-slate-50")
                }
              >
                <span className="font-semibold">{m.full_name}</span>
                <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                  {m.role.replace(/_/g, " ")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
